import React from 'react';
import { EDStyledMessageExpansionPanel } from '../../../BaseComponents/Accordion/ReadingListExpansionPanel/EDReadingListExpansionPanel';
import { IMessage } from '../Messages';
import EDTypography from '../../../BaseComponents/Typography/EDTypography';
import { Theme } from '@mui/material/styles';
import { withStyles } from 'tss-react/mui';
import EDIconButton from '../../../BaseComponents/Buttons/IconButton/EDIconButton';
import classNames from 'classnames';
import { MessageDeleteType } from '../Mutations/MessageListDeleteMutation';
import { useIsTouchDevice } from '../../BreakpointContext/BreakpointContextUtils';

interface IProps {
  expanded: boolean;
  message: IMessage;
  onUpdate: (message: IMessage, read?: boolean) => void;
  toggleRead: (message: IMessage) => void;
  delete: MessageDeleteType;
  classes?: any;
}

interface IState {
  expanded: boolean;
}

interface IInnerProps extends IProps {
  isTouchDevice: boolean;
}

const styles = (theme: Theme, _params: any, classes: any) => ({
  detailsRoot: {
    background: theme.palette.common.white,
    margin: '0 8px 8px',
    padding: '8px 24px',
    borderRadius: 4,
  },
  contentText: {
    width: '100%',
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
  },
  summaryContainer: {
    whiteSpace: 'nowrap',
    overflowX: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: 256,
  },
  toolbar: {
    position: 'absolute',
    top: 14,
    right: 4,
    display: 'flex',
    flexDirection: 'row',
    flex: '0 0 auto',
    width: 64,
    height: 32,
    alignItems: 'flex-start',
  },
  showIcon: {
    opacity: 1,
  },
  expandIcon: {
    color: theme.customProperties.MessageList.unreadIcon,
    '&:hover': {
      color: theme.customProperties.MessageList.unreadIconActive,
    },
  },
  expandedIcon: {
    color: theme.customProperties.MessageList.readIcon,
    '&:hover': {
      color: theme.customProperties.MessageList.readIconActive,
    },
  },
  root: {
    flex: '0 0 auto',
    width: '100%',
    background: theme.customProperties.MessageList.readBackground,
  },
  unreadRoot: {
    background: theme.customProperties.MessageList.unreadBackground,
  },
  summary: {
    backgroundColor: 'inherit',
  },
  summaryRoot: {
    alignItems: 'flex-start',
    minHeight: 68,
    borderRadius: 4,
    [`&.${classes.summaryExpanded}`]: {
      minHeight: 'initial',
    },
  },
  summaryExpanded: {
    margin: 0,
  },
  summaryContent: {
    margin: '4px 0 12px',
    '& >:last-child': {
      padding: 0,
    },
    [`&.${classes.summaryExpanded}`]: {
      margin: '4px 0 12px',
    },
  },
  typographyExpanded: {},
  typographyRead: {
    color: theme.palette.common.black,
  },
  messageTextColumn: {
    display: 'flex',
    flex: '1 1 auto',
    flexDirection: 'column',
    marginRight: 40,
    width: 192,
  },
  darkText: {
    color: theme.customProperties.MessageList.readMessageTextLow,
    '&:hover': {
      color: theme.customProperties.MessageList.readMessageTextHi,
    },
  },
  lightText: {
    color: theme.customProperties.MessageList.unreadMessageTextLow,
    '&:hover': {
      color: theme.customProperties.MessageList.unreadMessageTextHi,
    },
  },
  ellipsisOverflow: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  hiddenIcon: {
    opacity: 0,
    [`.${classes.summaryRoot}:hover &`]: {
      opacity: 1,
    },
  },
  dateText: {
    fontSize: '0.65rem',
    fontStyle: 'italic',
  },
});

class MessagesExpansionPanelBase extends React.Component<IInnerProps, IState> {
  constructor(props: IInnerProps) {
    super(props);

    this.state = {
      expanded: props.expanded,
    };
  }

  componentDidUpdate(prevProps: IProps) {
    if (prevProps.expanded !== this.props.expanded) {
      this.setState({
        expanded: this.props.expanded,
      });
    }
  }

  update = () => {
    this.setState(prevState => ({
      expanded: !prevState.expanded,
    }));
    const read = this.state.expanded ? this.props.message.reciever.read : true;
    if (read !== this.props.message.reciever.read) {
      this.props.onUpdate(this.props.message, read);
    }
  };

  toggleRead = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    this.props.toggleRead(this.props.message);
  };

  delete = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    this.props.delete({
      id: this.props.message.reciever.id,
      _version: this.props.message.reciever._version,
    });
  };

  summaryLinkClick = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  render() {
    const {
      contentText,
      summaryContainer,
      toolbar,
      expandIcon,
      expandedIcon,
      typographyExpanded,
      typographyRead,
      messageTextColumn,
      darkText,
      lightText,
      ellipsisOverflow,
      hiddenIcon,
      showIcon,
      dateText,
      unreadRoot,
      ...muiClasses
    } = this.props.classes;

    muiClasses.root = classNames(this.props.classes.root, {
      [this.props.classes.unreadRoot]: !this.props.message.reciever.read,
    });
    return (
      <>
        <EDStyledMessageExpansionPanel
          key={this.props.message.title}
          label={
            <>
              <div
                className={classNames(messageTextColumn, {
                  [darkText]: this.props.message.reciever.read,
                  [lightText]: !this.props.message.reciever.read,
                })}
              >
                <EDTypography
                  color="inherit"
                  variant="caption"
                  className={dateText}
                >
                  {this.props.message.createdDate.toLocaleDateString('sv-SE')}
                </EDTypography>
                <EDTypography
                  bold
                  color="inherit"
                  className={classNames(ellipsisOverflow, {
                    [typographyRead]: this.props.message.reciever.read,
                    [typographyExpanded]: this.state.expanded,
                  })}
                >
                  {this.props.message.title}
                </EDTypography>
                {!this.state.expanded && (
                  <EDTypography
                    color="inherit"
                    className={summaryContainer}
                    variant="body2"
                  >
                    {this.props.message.parts.map((part, index) => {
                      if (part.link) {
                        return (
                          <a
                            key={index}
                            href={
                              part.mail
                                ? `mailto:${part.text}`
                                : `http://${part.text}`
                            }
                            target={part.mail ? undefined : '_blank_'}
                            className={contentText}
                            onClick={this.summaryLinkClick}
                          >
                            {part.text}
                          </a>
                        );
                      }
                      return part.text;
                    })}
                  </EDTypography>
                )}
              </div>

              <div className={toolbar}>
                <EDIconButton
                  tooltip={
                    this.props.message.reciever.read
                      ? 'Markera som oläst'
                      : 'Markera som läst'
                  }
                  testId="read-icon"
                  icon={this.props.message.reciever.read ? 'drafts' : 'email'}
                  margin="none"
                  padding="dense"
                  onClick={this.toggleRead}
                  color="inherit"
                  className={
                    this.props.message.reciever.read ? expandedIcon : expandIcon
                  }
                />
                <EDIconButton
                  tooltip="Ta bort"
                  icon="delete"
                  margin="none"
                  padding="dense"
                  onClick={this.delete}
                  color="inherit"
                  className={classNames(hiddenIcon, {
                    [expandedIcon]: this.props.message.reciever.read,
                    [expandIcon]: !this.props.message.reciever.read,
                    [showIcon]: this.state.expanded || this.props.isTouchDevice,
                  })}
                />
              </div>
            </>
          }
          expanded={this.state.expanded}
          onChange={this.update}
          classes={muiClasses}
          elevation={0}
          changeSummaryStyleWhenExpanded
        >
          <div>
            {this.props.message.parts.map((part, index) =>
              part.link ? (
                <EDTypography
                  key={index}
                  component="span"
                  variant="body2"
                  className={contentText}
                >
                  <a
                    href={
                      part.mail ? `mailto:${part.text}` : `http://${part.text}`
                    }
                    target={part.mail ? undefined : '_blank_'}
                    className={contentText}
                  >
                    {part.text}
                  </a>
                </EDTypography>
              ) : (
                <EDTypography
                  key={index}
                  component="span"
                  variant="body2"
                  className={contentText}
                  useNewline
                >
                  {part.text}
                </EDTypography>
              )
            )}
          </div>
        </EDStyledMessageExpansionPanel>
      </>
    );
  }
}

const MessagesExpansionPanel: React.FC<
  React.PropsWithChildren<IProps>
> = props => {
  const isTouchDevice = useIsTouchDevice();
  return (
    <MessagesExpansionPanelBase {...props} isTouchDevice={isTouchDevice} />
  );
};

export default withStyles(MessagesExpansionPanel, styles, {
  uniqId: '42614dce-c171-49b3-aa52-d5a3e6882e2e',
});
